// DEPENDENCIES
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";

// CSS
import "./ProjectConsoleEditAddDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	getAPICall,
	postAPICall,
	putAPICall,
} from "../../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	ButtonGroup,
	CircularProgress,
	debounce,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormHelperText,
	FormLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";

// MUI ICONS
import CloseIcon from "@mui/icons-material/Close";

// CUSTOM COMPONENTS
import DropboxFileSelector from "../../../Misc/DropboxFileSelector/DropboxFileSelector";

// CONSTANTS
import { generateAlphanumericCode } from "../../../../constants/constants";
import { getYear } from "../../../../constants/dateTime";

// OTHER

// IDs of the OIC custom field's dropdown values in BQE — same hardcoded mapping
// PropertyConsoleEditAddDialog/PendingJobReviewScreen use when associating a
// newly-created BQE record's OIC.
const BQE_OIC_CUSTOM_FIELD_VALUES = {
	WA: "d5e226fe-5798-43a4-a752-7bcd087eeda3",
	OR: "03356b2a-8467-46ef-9ce7-cf4dc99599d3",
	UT: "60d08179-1541-41ee-938f-9415ba4b5872",
};

// Adds a "<code>.1 Videos" sibling under every "<code> Photos" folder in the given
// list — mirrors PendingJobReviewScreen's withVideosUnderPhotos convention.
const withVideosUnderPhotos = (paths) => {
	const results = [...paths];
	paths.forEach((p) => {
		const m = p.match(/\/(\d+\.\d+)\s+Photos$/);
		if (!m) return;
		results.push(`${p}/${m[1]}.1 Videos`);
	});
	return results;
};

// The "still looks selected" blue disabled style should only show when a toggle
// button is disabled because it's already the active mode. When it's disabled
// because a prerequisite hasn't been picked yet, let MUI's default grayed-out
// disabled style show instead — otherwise every disabled button looks "selected".
const activeModeButtonSx = (isDisabledBecauseActive) =>
	isDisabledBecauseActive
		? { "&.Mui-disabled": { background: "#184C93", color: "#ffffff" } }
		: undefined;

// REACT COMPONENT
const ProjectConsoleEditAddDialog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// REFS
	const hubSpotDealNameRef = useRef(null);
	// Guards fetchBqeParentPropertyRecord against out-of-order responses — if the
	// selected property changes again before an in-flight request resolves, the
	// stale response is discarded instead of overwriting the newer selection's data.
	const bqeParentPropertyRequestRef = useRef(0);

	// STATES
	const [loading, setLoading] = useState(true);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	// General inputs
	const [projectDropboxPath, setProjectDropboxPath] = useState("");
	const [projectProperty, setProjectProperty] = useState(null);
	const [projectBuildingComponents, setProjectBuildingComponents] = useState(
		[]
	);

	// BQE Project
	const [bqeProjectOptions, setBqeProjectOptions] = useState([]);
	const [bqeProjectSearchLoading, setBqeProjectSearchLoading] =
		useState(false);

	const [bqeProjectRecord, setBqeProjectRecord] = useState({});

	// "existing" reuses a BQE project found via search (original behavior); "create"
	// creates a brand-new BQE project (via /api/bqe/project/create) nested under the
	// selected property's own BQE record, inheriting its client/manager/class — there's
	// no "none" mode, a Tools project always needs a BQE project behind it.
	const [bqeProjectMode, setBqeProjectMode] = useState("existing");
	const [bqeParentPropertyRecord, setBqeParentPropertyRecord] = useState(null);
	const [bqeParentPropertyLoading, setBqeParentPropertyLoading] = useState(false);

	// "existing" browses/selects an existing Dropbox folder (original behavior); "create"
	// creates a brand-new folder, under the selected property's own Dropbox folder,
	// named "<building components> (<year>)" — matching PendingJobReviewScreen's
	// project-folder convention — plus its "1. General" subfolders. No "none" mode
	// and no free-text naming — a Tools project always needs a folder, auto-named.
	const [dropboxMode, setDropboxMode] = useState("existing");

	// HUBSPOT DEAL
	const [hubSpotDealOptions, setHubSpotDealOptions] = useState([]);
	const [hubSpotDealRecord, setHubSpotDealRecord] = useState({});
	const [hubSpotDealName, setHubSpotDealName] = useState("");
	const [hubSpotDealSearchLoading, setHubSpotDealSearchLoading] =
		useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// The BQE Project / Dropbox Folder "Create New" options both derive their name
	// (and, for BQE, their parent record) from the selected Tools property and
	// building components — so neither is usable until both are picked.
	const projectSelectionsComplete =
		Boolean(projectProperty) && projectBuildingComponents.length > 0;

	// USE EFFECT
	useEffect(() => {
		// populate fields if editing, or if adding with a prefill (the Project Viewer's
		// "Link Project to Tools" — an existing BQE project that has no Tools record yet)
		if (props.editing) {
			handlePopulateFieldsOnEdit(props.recordToEdit);
		} else if (props.open && props.prefillRecord) {
			handlePopulateFieldsOnEdit(props.prefillRecord);
		} else {
			setLoading(false);
		}
	}, [props.open, props.recordToEdit, props.prefillRecord]);

	// Refetch the parent property's raw BQE record whenever "Create New" BQE project
	// mode is active and the selected property changes, so the inherited
	// client/manager/class/address always match whichever property is selected.
	useEffect(() => {
		if (bqeProjectMode !== "create") return;
		fetchBqeParentPropertyRecord();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bqeProjectMode, projectProperty]);

	// If the property/building-component selections are cleared after a "Create New"
	// mode was already chosen, drop back to "existing" — the buttons that reach
	// "create" are disabled until both are selected, but this also covers
	// un-selecting afterward.
	useEffect(() => {
		if (projectSelectionsComplete) return;
		setBqeProjectMode((mode) => (mode === "create" ? "existing" : mode));
		setDropboxMode((mode) => (mode === "create" ? "existing" : mode));
	}, [projectSelectionsComplete]);

	// INPUT HANDLERS

	// BQE PROJECT MODE HANDLERS

	const bqeProjectExistingClicked = () => {
		setBqeProjectMode("existing");
		setBqeParentPropertyRecord(null);
	};

	const bqeProjectCreateClicked = () => {
		setBqeProjectMode("create");
		setBqeProjectRecord({});
		setBqeProjectOptions([]);
	};

	// DROPBOX MODE HANDLERS

	const dropboxExistingClicked = () => setDropboxMode("existing");

	const dropboxCreateClicked = () => setDropboxMode("create");

	const hubSpotDealNameSelected = (event, newValue) => {
		if (newValue && newValue.properties) {
			// if a deal name is selected and the user didn't just clear the autocomplete
			setHubSpotDealRecord(newValue);
			setHubSpotDealName(newValue.properties.dealname);
		} else {
			// if the user cleared the autocomplete
			setHubSpotDealRecord({});
			setHubSpotDealName("");
			setHubSpotDealOptions([]);
		}
	};

	const bqeProjectNameSelected = (event) => {
		const selectedId = event.target.value;
		const selectedProject = bqeProjectOptions.find(
			(option) => option.id === selectedId
		);

		if (selectedProject) {
			setBqeProjectRecord(selectedProject);
		} else {
			setBqeProjectRecord({});
			setBqeProjectOptions([]);
		}
	};

	// GENERAL INPUTS
	const handleProjectPropertyChange = (event, newValue) => {
		setProjectProperty(newValue);
		// if newValue is null, clear bqe project options
		if (!newValue) {
			setBqeProjectRecord({});
			setBqeProjectOptions([]);
			// clear dropbox path
			setProjectDropboxPath("");
		} else {
			// get bqe projects
			fetchBqeProjectRecords(newValue);
			// set dropbox path
			setProjectDropboxPath(newValue.dropbox_file_path);
		}
	};

	const handleProjectBuildingComponentsChange = (event, newValue) => {
		setProjectBuildingComponents(newValue);
	};

	// API FUNCTIONS

	// populate fields when editing (or prefilling an add)
	const handlePopulateFieldsOnEdit = async (record) => {
		setLoading(true);
		// editing always searches for existing BQE/Dropbox records — the Tools record
		// is already linked to real ones, so there's nothing to create
		setBqeProjectMode("existing");
		setBqeParentPropertyRecord(null);
		setDropboxMode("existing");
		// search though props.properties to find the property that matches the recordToEdit.propertyID
		const property = props.properties.find(
			(property) => property.id === record.property_id
		);
		setProjectProperty(property || null);
		// search existing hubspot deals
		if (record.hubspot_id) await fetchHubSpotDealRecord(record.hubspot_id);
		// reference display_name to get the building components, split by ', ', buildingcomponent.abbreviated_name
		const buildingComponents = (record.display_name || "").split(", ").filter(Boolean);
		const buildingComponentsArray = buildingComponents
			.map((component) =>
				props.buildingComponents.find(
					(buildingComponent) =>
						buildingComponent.abbreviated_name === component
				)
			)
			.filter(Boolean);
		setProjectBuildingComponents(buildingComponentsArray);
		// set bqe project options
		const bqeRecordsRes = (await fetchBqeProjectRecords(property)) || [];
		// set specific bqe project record
		const bqeProject = bqeRecordsRes.find(
			(option) => option.id === record.bqe_id
		);
		setBqeProjectRecord(bqeProject || {});
		if (!property) {
			setAlertMessage(
				"This project's linked property could not be found. Please re-select it before saving."
			);
			setAlertOpen(true);
		} else if (buildingComponentsArray.length < buildingComponents.length) {
			setAlertMessage(
				"Some of this project's building components could not be matched. Please re-select them before saving."
			);
			setAlertOpen(true);
		} else if (!bqeProject) {
			setAlertMessage(
				"Couldn't find this project's linked BQE project record. Please re-select it before saving."
			);
			setAlertOpen(true);
		}
		// set dropbox path
		setProjectDropboxPath(record.dropbox_path || "");
		setLoading(false);
	};

	const fetchHubSpotDealRecord = async (hubspotID) => {
		setHubSpotDealSearchLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hubspot/getDealFromDealID",
				{
					dealID: hubspotID,
				}
			);
			if (res.data) {
				hubSpotDealNameChanged({
					target: { value: res.data.properties.dealname },
				});
				hubSpotDealNameSelected(null, res.data);
			} else {
				setAlertMessage(
					"Error fetching HubSpot deal record. Record may not be associated with a HubSpot deal."
				);
				setAlertOpen(true);
			}
		} catch (error) {
			console.log("Error in fetchHubSpotDealRecord: ", error);
		}
		setHubSpotDealSearchLoading(false);
	};

	const hubSpotDealNameChanged = useMemo(
		() =>
			debounce(async (event) => {
				setHubSpotDealSearchLoading(true);
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setHubSpotDealSearchLoading(false);
					setHubSpotDealOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/search/deals",
						{
							dealName: event.target.value,
						}
					);

					setHubSpotDealOptions(res.data);
				} catch (error) {
					console.log("Error in hubspot dealNameChanged: ", error);
				}
				setHubSpotDealSearchLoading(false);
			}, 500),
		[]
	);

	const fetchBqeProjectRecords = async (newValue) => {
		setBqeProjectSearchLoading(true);
		// Return early if no bqe_id is present
		if (!newValue?.bqe_id) {
			setBqeProjectOptions([]);
			setBqeProjectSearchLoading(false);
			return [];
		}
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/project/byProperty",
				{
					propertyID: newValue.bqe_id,
				}
			);
			const filteredRecords =
				res.data == ""
					? []
					: res.data.filter(
							(option) =>
								!option.displayName.endsWith("(deleted)")
					  );
			setBqeProjectOptions(filteredRecords);
			setBqeProjectSearchLoading(false);
			return filteredRecords;
		} catch (error) {
			console.log("Error in fetch project records: ", error);
			setBqeProjectOptions([]);
			setBqeProjectSearchLoading(false);
			setAlertMessage(
				error?.message || "Error fetching BQE project records for this property"
			);
			setAlertOpen(true);
			return [];
		}
	};

	// fetches the raw BQE record for the selected property, used only in "Create New"
	// BQE project mode to inherit its client/manager/class/address
	const fetchBqeParentPropertyRecord = async () => {
		const requestId = ++bqeParentPropertyRequestRef.current;

		if (!projectProperty?.bqe_id) {
			setBqeParentPropertyRecord(null);
			return;
		}
		setBqeParentPropertyLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/property/byID",
				{ id: projectProperty.bqe_id }
			);
			if (requestId !== bqeParentPropertyRequestRef.current) return;
			setBqeParentPropertyRecord(res.data || null);
		} catch (error) {
			console.log("Error fetching parent BQE property record: ", error);
			if (requestId === bqeParentPropertyRequestRef.current) {
				setBqeParentPropertyRecord(null);
			}
		}
		if (requestId === bqeParentPropertyRequestRef.current) {
			setBqeParentPropertyLoading(false);
		}
	};

	// generates a unique BQE project code, retrying up to 10 times against the local
	// tools DB — mirrors PendingJobReviewScreen's generateProjectCode
	const generateUniqueBqeProjectCode = async () => {
		let projectCode = `PJ-${generateAlphanumericCode()}`;

		for (let attempt = 0; attempt < 10; attempt++) {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/byBQECode",
				{ bqeCode: projectCode }
			);

			if (res.data.length === 0) return projectCode;

			projectCode = `PJ-${generateAlphanumericCode()}`;
		}

		throw new Error("Failed to generate a unique BQE project code");
	};

	// best-effort — a failure here shouldn't undo the project record that was
	// already successfully created
	const associateBqeOicCustomField = async (bqeProjectId, oic) => {
		try {
			const bqeOICValue = BQE_OIC_CUSTOM_FIELD_VALUES[oic] || BQE_OIC_CUSTOM_FIELD_VALUES.WA;
			await postAPICall(instance, accounts[0], "/api/bqe/oic/assign", {
				bqeProjectId,
				bqeOICValue,
			});
		} catch (error) {
			console.log("Error associating BQE OIC custom field: ", error);
		}
	};

	// creates a brand-new BQE project nested under the selected property's own BQE
	// record, inheriting its client/manager/class instead of asking for them again
	const createBqeProjectRecord = async () => {
		if (!bqeParentPropertyRecord) {
			throw new Error("Missing the property's BQE record — select a property with a linked BQE record first");
		}

		const projectCode = await generateUniqueBqeProjectCode();
		const address = bqeParentPropertyRecord.address?.[0] || {};

		const res = await postAPICall(
			instance,
			accounts[0],
			"/api/bqe/project/create",
			{
				clientId: bqeParentPropertyRecord.clientId,
				projectCode,
				managerId: bqeParentPropertyRecord.managerId,
				projectName: projectBuildingComponents
					.map((component) => component.name)
					.join(", "),
				addressLine1: address.street1,
				addressLine2: address.street2,
				city: address.city,
				state: address.state,
				zip: address.zip,
				oic: projectProperty.oic,
				classId: bqeParentPropertyRecord.classId,
				parentId: bqeParentPropertyRecord.id,
				rootId: bqeParentPropertyRecord.id,
			}
		);
		if (!res.data || !res.data.id) {
			throw new Error("Failed to create BQE project record");
		}

		await associateBqeOicCustomField(res.data.id, projectProperty.oic);

		return res.data;
	};

	// mirrors PendingJobReviewScreen's project-folder naming convention:
	// "<abbreviated building components> (<year>)"
	const buildProjectFolderName = () =>
		`${projectBuildingComponents.map((c) => c.abbreviated_name).join(", ")} (${getYear()})`;

	// resolves the dropbox path to save for this project: the browsed-to existing
	// folder, or a brand-new folder — auto-named, under the selected property's own
	// Dropbox folder — with its "1. General" subfolders
	const resolveDropboxPath = async () => {
		if (dropboxMode === "existing") return projectDropboxPath || null;

		const parent = projectProperty?.dropbox_file_path || "";
		const newPath = `${parent}/${buildProjectFolderName()}`.replace(/\/{2,}/g, "/");

		const createRes = await postAPICall(
			instance,
			accounts[0],
			"/api/dropbox/create/folder",
			{ path: newPath }
		);
		if (!createRes.data || createRes.data.error) {
			throw new Error("Failed to create Dropbox folder");
		}

		// "1. General" is a project-wide folder, independent of which phases exist yet
		const generalPaths = withVideosUnderPhotos([
			`${newPath}/1. General/1.0 Photos`,
			`${newPath}/1. General/1.1 Admin`,
			`${newPath}/1. General/1.2 Docs In`,
			`${newPath}/1. General/1.3 Engineering`,
		]);
		const batchRes = await postAPICall(
			instance,
			accounts[0],
			"/api/dropbox/create/folder/batch",
			{ paths: generalPaths }
		);
		if (batchRes.data && batchRes.data.error) {
			throw new Error(
				'Project folder created, but failed to create its "1. General" subfolders'
			);
		}

		return newPath;
	};

	const handleAddRecord = async () => {
		setLoading(true);

		try {
			const bqeProjectForSave =
				bqeProjectMode === "create"
					? await createBqeProjectRecord()
					: bqeProjectRecord;

			const resolvedDropboxPath = await resolveDropboxPath();

			await postAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/create",
				{
					name: projectBuildingComponents
						.map((component) => component.name)
						.join(", "),
					displayName: projectBuildingComponents
						.map((component) => component.abbreviated_name)
						.join(", "),
					dropboxPath: resolvedDropboxPath,
					bqeID: bqeProjectForSave.id,
					bqeCode: bqeProjectForSave.code,
					hubspotID: hubSpotDealRecord.properties.hs_object_id,
					quoteID: hubSpotDealRecord.properties.hs_quote_id
						? hubSpotDealRecord.properties.hs_quote_id
						: "",
					propertyID: projectProperty.id,
					ownerID: getEmployeeIDFromHubSpotID(
						hubSpotDealRecord.properties.hubspot_owner_id
					),
					modifiedBy: employeeID,
				}
			);
			setLoading(false);
			return true;
		} catch (error) {
			console.log(error);
			setAlertMessage(error?.message || "Error adding record");
			setAlertOpen(true);
			setLoading(false);
			return false;
		}
	};

	const handleEditRecord = async () => {
		setLoading(true);
		try {
			const bqeProjectForSave =
				bqeProjectMode === "create"
					? await createBqeProjectRecord()
					: bqeProjectRecord;

			const resolvedDropboxPath = await resolveDropboxPath();

			await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/project/update",
				{
					id: props.recordToEdit.id,
					name: projectBuildingComponents
						.map((component) => component.name)
						.join(", "),
					displayName: projectBuildingComponents
						.map((component) => component.abbreviated_name)
						.join(", "),
					dropboxPath: resolvedDropboxPath,
					bqeID: bqeProjectForSave.id,
					bqeCode: bqeProjectForSave.code,
					hubspotID: hubSpotDealRecord.properties.hs_object_id,
					quoteID: hubSpotDealRecord.properties.hs_quote_id
						? hubSpotDealRecord.properties.hs_quote_id
						: "",
					propertyID: projectProperty.id,
					ownerID: getEmployeeIDFromHubSpotID(
						hubSpotDealRecord.properties.hubspot_owner_id
					),
					modifiedBy: employeeID,
				}
			);
			setLoading(false);
			return true;
		} catch (error) {
			console.log(error);
			setAlertMessage(error?.message || "Error editing record");
			setAlertOpen(true);
			setLoading(false);
			return false;
		}
	};

	// HELPER FUNCTIONS

	const handleSubmitClick = async () => {
		if (!validateInputFields()) return;
		setLoading(true);
		// if editing, update record, else add record
		const succeeded = props.editing
			? await handleEditRecord()
			: await handleAddRecord();

		// Leave the dialog open with the error alert visible on failure —
		// closing here would wipe the alert before the user ever saw it.
		if (!succeeded) return;

		resetInputFields();
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose();
	};

	const handleCancelClick = () => {
		resetInputFields();
		setAlertMessage("");
		setAlertOpen(false);
		props.onCloseNoRefresh();
	};

	const resetInputFields = () => {
		setLoading(false);
		setAlertMessage("");
		setAlertOpen(false);

		setBqeProjectOptions([]);
		setBqeProjectSearchLoading(false);
		setBqeProjectRecord({});

		setBqeProjectMode("existing");
		setBqeParentPropertyRecord(null);
		setBqeParentPropertyLoading(false);

		setDropboxMode("existing");

		setHubSpotDealOptions([]);
		setHubSpotDealRecord({});
		setHubSpotDealName("");
		setHubSpotDealSearchLoading(false);

		setProjectDropboxPath("");
		setProjectProperty(null);

		setProjectBuildingComponents([]);
	};

	const validateInputFields = () => {
		if (
			!projectBuildingComponents ||
			projectBuildingComponents.length === 0
		) {
			setAlertMessage("Please select building components");
			setAlertOpen(true);
			return false;
		}
		if (!projectProperty) {
			setAlertMessage("Please select a property");
			setAlertOpen(true);
			return false;
		}
		if (bqeProjectMode === "existing" && !bqeProjectRecord.id) {
			setAlertMessage("Please select a BQE project");
			setAlertOpen(true);
			return false;
		}
		if (bqeProjectMode === "create" && !bqeParentPropertyRecord) {
			setAlertMessage(
				"The selected property doesn't have a usable BQE record to create a new BQE project under"
			);
			setAlertOpen(true);
			return false;
		}
		if (!hubSpotDealRecord.properties) {
			setAlertMessage("Please select a HubSpot deal");
			setAlertOpen(true);
			return false;
		}
		if (dropboxMode === "existing" && projectDropboxPath === "") {
			setAlertMessage("Please enter a Dropbox path");
			setAlertOpen(true);
			return false;
		}
		if (dropboxMode === "create" && !projectProperty?.dropbox_file_path) {
			setAlertMessage(
				"The selected property doesn't have a Dropbox folder to create a project folder under"
			);
			setAlertOpen(true);
			return false;
		}

		return true;
	};

	const getEmployeeNameFromHubSpotID = (hubspotID) => {
		if (!hubspotID) return "";
		const employee = props.employees.find(
			(employee) => employee.hubspot_id == hubspotID
		);
		return employee ? employee.first_name + " " + employee.last_name : "";
	};

	const getEmployeeIDFromHubSpotID = (hubspotID) => {
		if (!hubspotID) return "";
		const employee = props.employees.find(
			(employee) => employee.hubspot_id == hubspotID
		);
		return employee ? employee.id : "";
	};

	// RENDER
	return (
		<Dialog
			id="ProjectConsoleEditAddDialog"
			open={props.open}
			fullWidth
			maxWidth="xl"
			onClose={handleCancelClick}
		>
			<DialogTitle id="ProjectConsoleEditAddDialogTitle">
				{/* edit / add */}
				{props.editing
					? "Edit Project"
					: props.prefillRecord
					? "Link Project to Tools"
					: "Add Project"}
			</DialogTitle>
			{loading && (
				<div className="projectConsoleLoadingSpinner">
					<CircularProgress />
				</div>
			)}
			{!loading && (
				<DialogContent id="ProjectConsoleEditAddDialogContent">
					{alertOpen && (
						<Alert
							severity="warning"
							action={
								<IconButton
									aria-label="close"
									color="inherit"
									size="small"
									onClick={() => {
										setAlertOpen(false);
									}}
								>
									<CloseIcon />
								</IconButton>
							}
						>
							{alertMessage}
						</Alert>
					)}

					<div className="dialogSubtitle">Tools Property Record</div>

					<FormControl sx={{ flex: 1 }}>
						<div className="FormBlock">
							<div className="FormRow">
								<Autocomplete
									sx={{ flexGrow: 1 }}
									id="propertyID"
									options={props.properties}
									getOptionLabel={(option) =>
										option ? option.name : ""
									}
									value={projectProperty}
									onChange={handleProjectPropertyChange}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Property"
											variant="outlined"
										/>
									)}
								/>
							</div>
						</div>
					</FormControl>

					<Divider />
					<div className="dialogSubtitle">
						Search Existing HubSpot Deals
					</div>

					{/* HUBSPOT */}
					<FormControl sx={{ flex: 1 }}>
						<div className="FormBlock">
							<div className="FormRow">
								<Autocomplete
									sx={{ flexGrow: 1 }}
									options={hubSpotDealOptions}
									ref={hubSpotDealNameRef}
									getOptionLabel={(option) =>
										option.properties
											? option.properties.dealname
											: option
									}
									value={
										hubSpotDealOptions.find(
											(option) =>
												option.properties?.dealname ===
												hubSpotDealName
										) || null
									}
									filterSelectedOptions
									onChange={hubSpotDealNameSelected}
									clearOnBlur={false}
									renderInput={(params) => (
										<TextField
											{...params}
											label="HubSpot Deal Name"
											placeholder="HubSpot Deal Name"
											value={hubSpotDealName || ""}
											onKeyUp={hubSpotDealNameChanged}
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{hubSpotDealSearchLoading && (
															<CircularProgress
																color="inherit"
																size={20}
															/>
														)}
													</>
												),
											}}
										/>
									)}
								/>
							</div>
							<div className="FormRow">
								<TextField
									sx={{ flexGrow: 1 }}
									label="Owner"
									variant="outlined"
									value={
										hubSpotDealRecord.properties
											?.hubspot_owner_id
											? getEmployeeNameFromHubSpotID(
													hubSpotDealRecord.properties
														.hubspot_owner_id
											  )
											: ""
									}
									disabled
									helperText={
										hubSpotDealName &&
										!hubSpotDealRecord.properties
											.hubspot_owner_id
											? "No employee associated with deal"
											: "This field is auto-populated from HubSpot"
									}
									error={Boolean(
										hubSpotDealName &&
											!hubSpotDealRecord.properties
												.hubspot_owner_id
									)}
								/>
							</div>
						</div>
					</FormControl>

					<Divider />
					<div className="dialogSubtitle">Building Components</div>

					<FormControl sx={{ flex: 1 }}>
						<div className="FormBlock">
							<div className="FormRow">
								<Autocomplete
									sx={{ flexGrow: 1 }}
									multiple
									filterSelectedOptions
									id="buildingComponents"
									options={props.buildingComponents}
									getOptionLabel={(option) => option.name}
									value={projectBuildingComponents}
									onChange={(event, newValue) => {
										// Allow only up to 2 selections
										if (newValue.length <= 2) {
											handleProjectBuildingComponentsChange(
												event,
												newValue
											);
										} else {
											setAlertMessage(
												"Please select up to 2 building components"
											);
											setAlertOpen(true);
										}
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Building Components"
											variant="outlined"
										/>
									)}
								/>
							</div>
						</div>
					</FormControl>

					{/* BQE Project */}
					<Divider />
					<div className="dialogSubtitleWithActions">
						<span>BQE Project</span>
						{!props.editing && (
							<ButtonGroup size="small">
								<Button
									sx={activeModeButtonSx(
										projectSelectionsComplete && bqeProjectMode === "existing"
									)}
									variant={bqeProjectMode === "existing" ? "contained" : "outlined"}
									onClick={bqeProjectExistingClicked}
									disabled={bqeProjectMode === "existing" || !projectSelectionsComplete}
								>
									Search Existing
								</Button>
								<Button
									sx={activeModeButtonSx(
										projectSelectionsComplete && bqeProjectMode === "create"
									)}
									variant={bqeProjectMode === "create" ? "contained" : "outlined"}
									onClick={bqeProjectCreateClicked}
									disabled={bqeProjectMode === "create" || !projectSelectionsComplete}
								>
									Create New
								</Button>
							</ButtonGroup>
						)}
					</div>

					{bqeProjectMode === "existing" && projectSelectionsComplete && (
						<FormControl
							sx={{ flex: 1 }}
							error={Boolean(
								bqeProjectOptions.length === 0 &&
									!bqeProjectSearchLoading &&
									projectProperty
							)}
						>
							<div className="FormBlock">
								<div className="FormRow">
									<FormControl sx={{ flexGrow: 1 }}>
										<InputLabel id="projectRecordLabel">
											BQE Project
										</InputLabel>
										<Select
											labelId="projectRecordLabel"
											value={bqeProjectRecord.id || ""}
											label="BQE Project"
											onChange={bqeProjectNameSelected}
											disabled={bqeProjectSearchLoading}
											startAdornment={
												<InputAdornment position="start">
													{bqeProjectSearchLoading && (
														<CircularProgress
															size={20}
														/>
													)}
												</InputAdornment>
											}
										>
											{bqeProjectOptions.map((option) => (
												<MenuItem
													key={option.id}
													value={option.id}
												>
													{option.displayName}
												</MenuItem>
											))}
										</Select>
										{bqeProjectOptions.length === 0 &&
											!bqeProjectSearchLoading && (
												<FormHelperText>
													{projectProperty?.bqe_id
														? `No existing BQE projects found under property BQE ID ${projectProperty.bqe_id}.`
														: "This property has no BQE ID on file, so its BQE projects can't be looked up."}
												</FormHelperText>
											)}
									</FormControl>
								</div>
							</div>
						</FormControl>
					)}

					{bqeProjectMode === "create" && projectSelectionsComplete && (
						<FormControl sx={{ flex: 1 }}>
							{bqeParentPropertyLoading && (
								<div className="FormRow">
									<CircularProgress size={20} />
								</div>
							)}
							{bqeParentPropertyRecord && (
								<FormLabel>
									A new BQE project will be created under "
									{bqeParentPropertyRecord.displayName}" named "
									{projectBuildingComponents
										.map((component) => component.name)
										.join(", ")}
									".
								</FormLabel>
							)}
						</FormControl>
					)}

					<Divider />
					<div className="dialogSubtitleWithActions">
						<span>Dropbox Folder</span>
						{!props.editing && (
							<ButtonGroup size="small">
								<Button
									sx={activeModeButtonSx(
										projectSelectionsComplete && dropboxMode === "existing"
									)}
									variant={dropboxMode === "existing" ? "contained" : "outlined"}
									onClick={dropboxExistingClicked}
									disabled={dropboxMode === "existing" || !projectSelectionsComplete}
								>
									Search Existing
								</Button>
								<Button
									sx={activeModeButtonSx(
										projectSelectionsComplete && dropboxMode === "create"
									)}
									variant={dropboxMode === "create" ? "contained" : "outlined"}
									onClick={dropboxCreateClicked}
									disabled={dropboxMode === "create" || !projectSelectionsComplete}
								>
									Create New
								</Button>
							</ButtonGroup>
						)}
					</div>

					{dropboxMode === "existing" && projectSelectionsComplete && (
						<FormControl sx={{ flex: 1 }}>
							<div className="FormBlock">
								<div className="FormRow">
									<DropboxFileSelector
										filePath={projectDropboxPath}
										setFilePath={setProjectDropboxPath}
									/>
								</div>
							</div>
						</FormControl>
					)}

					{dropboxMode === "create" && projectSelectionsComplete && projectProperty?.dropbox_file_path && (
						<FormControl sx={{ flex: 1 }}>
							<FormLabel>
								A new project folder, with its "1. General"
								subfolders, will be created at:{" "}
								{`${projectProperty.dropbox_file_path}/${buildProjectFolderName()}`.replace(
									/\/{2,}/g,
									"/"
								)}
							</FormLabel>
						</FormControl>
					)}

					<div className="editProjectConsoleButtons">
						<Button
							variant="outlined"
							color="error"
							onClick={handleCancelClick}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleSubmitClick}
							disabled={loading}
						>
							Submit
						</Button>
					</div>
				</DialogContent>
			)}
		</Dialog>
	);
};

// EXPORT
export default ProjectConsoleEditAddDialog;
